import { endpoints, envStore } from '@/env'
import { toQueryString } from '@/graphql/utils'
import { MutationOptions } from '@/hooks/useMutation'
import camelcase from '@/utils/camelcase'
import message, { error } from '@/utils/message'
import {
  __,
  allPass,
  always,
  and,
  equals,
  identity,
  ifElse,
  includes,
  invoker,
  is,
  isNil,
  merge,
  mergeDeepRight,
  not,
  path,
  pathOr,
  T,
} from 'ramda'
import React from 'react'
import Store from 'store'
const { compose, cond } = require('ramda')

/**
 * 替換Url中的變數，例如/v1/api/user/{id} 轉換成 /v1/api/user/5
 * @param url API Url
 * @param body Query Parameters
 */
export function format(url: string, params: { [key: string]: any } = {}) {
  const regex = /(\{.+?\})/gi
  const _params = { ...params }

  const _url = url.replace(regex, v => {
    const replacable = v[0] === '{'

    if (!replacable) {
      return v
    }

    const propName = v.slice(1, -1)
    const replacedValue = _params[propName]

    _params[propName] = undefined

    return replacedValue
  })

  return { fullUrl: _url, params: _params }
}

interface RequestInit
  extends Omit<MutationOptions, 'onError' | 'onCompleted' | 'refetchQueries'> {
  body?: any
  credentials?: RequestCredentials_
  headers?: HeadersInit_
  integrity?: string
  keepalive?: boolean
  method?: 'DELETE' | 'PUT' | 'PATCH' | 'GET' | 'POST'
  mode?: RequestMode_
  referrer?: string
  window?: any
  errorMsg?: boolean
  throwable?: boolean
  withToken?: boolean
  endpoint?: 'apiBaseUrl' | 'fakeApiBaseUrl'
  signal?: AbortSignal | null | undefined
  onError?: (err: Error) => any
  onCompleted?: (data: any) => any
}

export function useMeta() {
  const [loading, setIsLoading] = React.useState(false)

  const withMeta = React.useCallback((handler: any) => {
    setIsLoading(true)

    return handler()
      .then((response: any) => response)
      .finally(() => setIsLoading(false))
  }, [])

  return [loading, withMeta] as [boolean, (handler: any) => any]
}

interface UseRequestOptions extends RequestInit {
  lazy?: boolean
  variables?: { [key: string]: any }
}

export function useRequest<T = any>(
  url: string,
  { lazy = true, variables = {}, ..._options }: UseRequestOptions = {}
) {
  const called = React.useRef(false)

  const [loading, withMeta] = useMeta()

  const [data, setData] = React.useState<T>()

  const [error, setError] = React.useState<Error>()

  const controllerRef = React.useRef<AbortController | null>(null)

  React.useEffect(() => {
    return function cleanup() {
      if (controllerRef.current && loading) {
        controllerRef.current.abort()
      }
    }
  }, [])

  const _refetch = (data: { [key: string]: any } = {}) => {
    let { fullUrl, params } = format(url, mergeDeepRight(variables, data))

    if (_options.method === 'GET') {
      fullUrl = `${fullUrl}?${toQueryString(params)}`
    }

    if (controllerRef.current) {
      controllerRef.current.abort()
    }

    controllerRef.current = new AbortController()

    return withMeta(async () => {
      const response = await request<T>(fullUrl, {
        ..._options,
        ...(_options.method !== 'GET' ? { body: data } : {}),
        signal: controllerRef.current?.signal,
        onCompleted: response => {
          if (_options.onCompleted) {
            _options.onCompleted({ data, response })
          }

          setData(response)
        },
        onError: error => {
          if (_options.onError) {
            _options.onError(error)
          }

          setError(error)
        },
      })

      controllerRef.current = null

      return response
    })
  }

  React.useEffect(() => {
    if (!lazy && !called.current) {
      _refetch()

      called.current = true
    }
  }, [lazy, called]) // eslint-disable-line

  return {
    loading,
    mutate: _refetch as (
      v?: { [key: string]: any } | { [key: string]: any }[]
    ) => Promise<T>,
    data,
    error,
    called,
  }
}

const contentTypeIs = (s: string) => {
  return compose(
    ifElse(isNil, identity, (c = '') => c.includes(s)),
    invoker(1, 'get')('content-type'),
    path(['headers'])
  )
}

const convertObjectKeys: any = (
  object: any[] | { [key: string]: any },
  keypath: string[] = []
) => {
  if (!object || typeof object !== 'object') {
    return object
  }

  if (Array.isArray(object)) {
    return object.map((o, index) =>
      convertObjectKeys(o, [...keypath, String(index)])
    )
  }

  return Object.keys(object).reduce((acc: any, key: string) => {
    let value = object[key]

    const nestedKeyPath = [...keypath, key]

    acc[camelcase(key)] = convertObjectKeys(value, nestedKeyPath)
    return acc
  }, {})
}

export function request<T = any>(
  url: string,
  {
    body,
    headers,
    msg = true,
    errorMsg = true,
    withToken = true,
    throwable = false,
    successMessage,
    onCompleted,
    onError,
    endpoint,
    ...options
  }: RequestInit = {}
): Promise<T> {
  const fullUrl = cond([
    [includes('http'), always(url)],
    [
      () => compose(not, isNil)(endpoint),
      always(pathOr(envStore.apiBaseUrl, [endpoint as any], endpoints) + url),
    ],
    [T, always(envStore.apiBaseUrl + url)],
  ])(url)

  return fetch(fullUrl, {
    ...options,
    body: body instanceof FormData ? body : JSON.stringify(body),
    headers: merge(
      {
        ...(withToken
          ? {
              Authorization: `Bearer ${Store.get(
                'scnt_lightinternal_token'
              )}` as NonNullable<string>,
            }
          : {}),
      },
      headers
        ? { ...headers }
        : { 'Content-Type': 'application/json', Accept: 'application/json' }
    ) as Record<string, string>,
  })
    .then(async (response: Response) => {
      if (
        compose(
          and(msg),
          allPass([compose(not, isNil), compose(not, equals(__, 'GET'))]),
          path(['method'])
        )(options)
      ) {
        message({
          content: successMessage || '資料已更新成功',
          type: 'success',
        })
      }

      let result: any

      if (response.status === 404) {
        return Promise.reject(response.statusText)
      }

      if (contentTypeIs('application/json')(response)) {
        result = await response.json()

        if (!response.ok) {
          return Promise.reject(result)
        }

        result = convertObjectKeys(result)
      }

      if (contentTypeIs('sheet')(response)) {
        result = await response.blob()
      }

      if (isNil(result)) {
        result = await response.text()
      }

      if (onCompleted) {
        onCompleted(result)
      }

      return result
    })
    .catch((err: Error) => {
      if (errorMsg) {
        error(err)
      }

      if (onError) {
        onError(err)
      }

      if (throwable) {
        throw new Error(
          err.message || is(String, err) ? err : ('Server Error' as any)
        )
      }

      return err
    })
}

export default request
